/**
 * Softphone Component
 * Browser-based phone using Twilio Voice SDK
 * Allows agents to receive and make calls directly in the browser
 */

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Collapse,
  Badge,
  Divider,
  Stack,
  InputAdornment,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneDisabled as HangupIcon,
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Dialpad as DialpadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhoneCallback as IncomingIcon,
  PhoneForwarded as OutgoingIcon,
  Circle as CircleIcon,
  Settings as SettingsIcon,
  Backspace as BackspaceIcon
} from '@mui/icons-material';
import { Device } from '@twilio/voice-sdk';
import callCentreService from '../../services/callCentreService';
import { createTokenRefreshController } from './tokenRefreshController';
import { totalsByCurrency } from '../../utils/currencies';

// Softphone states
const STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  RINGING: 'ringing',
  CONNECTING: 'connecting',
  ON_CALL: 'on_call',
  ERROR: 'error',
  OFFLINE: 'offline'
};

// The popup is written with document.write, so every value goes in as text: the customer's name and email come
// from Shopify order addresses the shopper typed (DT-1109).
export const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

// Build popup HTML content
export function buildPopupContent({ from, callerDisplay, brandName, loading, customer }) {
  const customerSection = loading ? `
    <div class="loading">Loading customer info...</div>
  ` : (customer ? `
    <div class="customer-info">
      <div class="customer-name">${escapeHtml(customer.name || 'Unknown')}</div>
      ${customer.email ? `<div class="customer-email">${escapeHtml(customer.email)}</div>` : ''}
      <div class="customer-stats">
        <div class="stat">
          <div class="stat-value">${escapeHtml(customer.orderCount)}</div>
          <div class="stat-label">Orders</div>
        </div>
        <div class="stat">
          <div class="stat-value">${escapeHtml(customer.totalSpent)}</div>
          <div class="stat-label">Lifetime Value</div>
        </div>
      </div>
      ${customer.lastOrderDate ? `<div class="last-order">Last order: ${escapeHtml(customer.lastOrderDate)}</div>` : ''}
    </div>
  ` : `
    <div class="new-customer">🆕 New Customer</div>
  `);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>📞 Incoming Call</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
          color: white;
          margin: 0;
          padding: 15px;
          min-height: calc(100vh - 30px);
        }
        .header { text-align: center; margin-bottom: 15px; }
        h2 { margin: 0 0 5px 0; font-size: 16px; opacity: 0.9; }
        .brand {
          background: rgba(255,255,255,0.2);
          padding: 4px 12px;
          border-radius: 12px;
          display: inline-block;
          font-size: 12px;
          margin-bottom: 10px;
        }
        .phone { font-size: 18px; font-weight: 600; }
        .customer-info {
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          padding: 12px;
          margin: 10px 0;
        }
        .customer-name { font-size: 16px; font-weight: 600; margin-bottom: 4px; }
        .customer-email { font-size: 12px; opacity: 0.8; margin-bottom: 10px; }
        .customer-stats { display: flex; justify-content: space-around; margin: 10px 0; }
        .stat { text-align: center; }
        .stat-value { font-size: 18px; font-weight: 700; }
        .stat-label { font-size: 10px; opacity: 0.8; text-transform: uppercase; }
        .last-order { font-size: 11px; opacity: 0.7; text-align: center; margin-top: 8px; }
        .new-customer {
          text-align: center;
          padding: 15px;
          font-size: 14px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          margin: 10px 0;
        }
        .loading { text-align: center; padding: 20px; opacity: 0.8; }
        button {
          width: 100%;
          padding: 12px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          margin-top: 10px;
        }
        .answer { background: #4caf50; color: white; }
        .answer:hover { background: #43a047; }
      </style>
    </head>
    <body>
      <div class="header">
        <h2>📞 INCOMING CALL</h2>
        <div class="brand">${escapeHtml(brandName)}</div>
        <div class="phone">${escapeHtml(from)}</div>
      </div>
      ${customerSection}
      <button class="answer" onclick="window.opener.focus(); window.close();">
        Go to Call Centre
      </button>
    </body>
    </html>
  `;
}

const Softphone = forwardRef(({ slackUserId, selectedBrandId, brandPhoneNumbers, onCallStart, onCallEnd, onIncomingCall }, ref) => {
  // Device and call state
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [state, setState] = useState(STATES.INITIALIZING);
  const [error, setError] = useState(null);
  const [selectedCallerId, setSelectedCallerId] = useState('');

  // Track page load time for auto-answer safety window
  const pageLoadTimeRef = useRef(Date.now());
  const autoAnswerConsumedRef = useRef(false);

  // Call info
  const [callDirection, setCallDirection] = useState(null); // 'inbound' | 'outbound'
  const [callerInfo, setCallerInfo] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  const [activeCallerId, setActiveCallerId] = useState(null); // The "from" number used for outbound calls

  // Controls
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);

  // UI state
  const [dialpadOpen, setDialpadOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [inputDigits, setInputDigits] = useState('');

  // Refs
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  // One token lifecycle per Device — see tokenRefreshController.js (2026-08-18 token-storm incident)
  const tokenCtlRef = useRef(null);
  // Consecutive "token expired/invalid-expiry" (20104/31205) device errors since the last
  // successful registration. A fresh mint should clear it; if it doesn't after 3 tries the
  // server is issuing tokens Twilio rejects (clock skew / key mismatch) → stop and tell the user.
  const tokenExpiredStreakRef = useRef(0);
  // Lifecycle guards for initializeDevice(): never two inits in flight, never an init
  // after unmount, and at most ONE pending init-retry timer (tracked so cleanup can clear it).
  const mountedRef = useRef(false);
  const initInFlightRef = useRef(false);
  const initRetryTimerRef = useRef(null);
  const ringtoneRef = useRef(null);
  const originalTitleRef = useRef(document.title);
  const keepAliveRef = useRef(null);
  const reconnectionStateRef = useRef({ isReconnecting: false, attempt: 0 });
  const resolvedAgentIdRef = useRef(null); // Real agent ID from Twilio token (resolves 'email_lookup')

  // Ref to hold makeCall function for external access
  const makeCallRef = useRef(null);

  // Expose makeCall to parent components via ref
  useImperativeHandle(ref, () => ({
    makeCall: (number) => {
      if (makeCallRef.current) {
        makeCallRef.current(number);
      }
    },
    makeCallWithCallerId: (number, callerId) => {
      if (makeCallRef.current) {
        makeCallRef.current(number, callerId);
      }
    },
    isReady: () => state === STATES.READY,
    getState: () => state
  }), [state]);

  // Auto-select first phone number when brand phone numbers change
  useEffect(() => {
    if (brandPhoneNumbers && brandPhoneNumbers.length > 0) {
      setSelectedCallerId(brandPhoneNumbers[0].phone_number);
    } else {
      setSelectedCallerId('');
    }
  }, [brandPhoneNumbers]);

  // Initialize Twilio Device
  useEffect(() => {
    mountedRef.current = true;
    initializeDevice();

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [slackUserId]);

  const cleanup = () => {
    reconnectionStateRef.current.isReconnecting = false;
    reconnectionStateRef.current.attempt = 0;
    if (initRetryTimerRef.current) {
      clearTimeout(initRetryTimerRef.current);
      initRetryTimerRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (tokenCtlRef.current) {
      tokenCtlRef.current.dispose();
      tokenCtlRef.current = null;
    }
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null; // a late token fetch must not updateToken() a destroyed Device
    }
    // Remove beforeunload handler
    if (window._softphoneBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', window._softphoneBeforeUnloadHandler);
    }
    // Remove visibility change handler
    if (window._softphoneVisibilityHandler) {
      document.removeEventListener('visibilitychange', window._softphoneVisibilityHandler);
    }
    // Remove online handler
    if (window._softphoneOnlineHandler) {
      window.removeEventListener('online', window._softphoneOnlineHandler);
    }
    // Stop Web Worker
    if (window._softphoneKeepAliveWorker) {
      window._softphoneKeepAliveWorker.postMessage('stop');
      window._softphoneKeepAliveWorker.terminate();
    }
    if (window._softphoneWorkerUrl) {
      URL.revokeObjectURL(window._softphoneWorkerUrl);
    }
    // Mark offline on cleanup (but don't block - use fire and forget)
    if (slackUserId) {
      callCentreService.markOffline(slackUserId, 'browser').catch(() => {});
    }
    stopRingtone();
    stopTitleFlash();
  };

  // Send heartbeat to server for presence tracking
  const sendServerHeartbeat = async (identity) => {
    try {
      const agentId = identity.replace('agent_', '');
      await callCentreService.sendHeartbeat(agentId, 'browser');
      console.log('[Softphone] Server heartbeat sent for:', agentId);
    } catch (err) {
      // Don't fail loudly - heartbeat failures shouldn't break the softphone
      console.log('[Softphone] Heartbeat failed (non-critical):', err.message);
    }
  };

  // Unified recovery strategy — single entry point for ALL reconnection paths
  const attemptRecovery = async (trigger, errorCode = null) => {
    if (!mountedRef.current) return; // never run recovery for an unmounted softphone
    const rs = reconnectionStateRef.current;

    // Guard: prevent concurrent recovery attempts
    if (rs.isReconnecting) {
      console.log(`[Softphone] Recovery already in progress, ignoring trigger: ${trigger}`);
      return;
    }

    // Don't trigger device recovery during active calls — let call-level reconnecting handle it
    if (callRef.current) {
      console.log(`[Softphone] Skipping device recovery during active call, trigger: ${trigger}`);
      return;
    }

    // Fatal errors — require user intervention, don't auto-retry.
    // (20104/31205 token-EXPIRED are recoverable: the device error handler mints a fresh token first.)
    const isFatal = errorCode === 20101 || errorCode === 31204;
    if (isFatal) {
      console.log(`[Softphone] Fatal error (${errorCode}), user intervention required`);
      setState(STATES.ERROR);
      setError('Session expired. Please refresh the page to reconnect.');
      return;
    }

    rs.isReconnecting = true;
    rs.attempt += 1;
    const attempt = rs.attempt;

    console.log(`[Softphone] Recovery attempt ${attempt} from: ${trigger}`);

    // Show reconnecting state (not ERROR — this is auto-recovery)
    setState(STATES.OFFLINE);
    setError(`Reconnecting... (attempt ${attempt})`);

    // Backoff: 2s, 4s, 8s, 16s, 32s, 60s (capped)
    const delay = Math.min(2000 * Math.pow(2, attempt - 1), 60000);
    await new Promise(resolve => setTimeout(resolve, delay));
    if (!mountedRef.current) { rs.isReconnecting = false; return; }

    // Abort if device was destroyed during delay (e.g., user navigated away)
    if (!deviceRef.current && trigger !== 'manual-retry' && trigger !== 'visibility-change-no-device' && trigger !== 'network-online-no-device') {
      console.log('[Softphone] No device after delay, will reinitialize');
      try {
        await reinitializeDevice();
        rs.isReconnecting = false;
        rs.attempt = 0;
      } catch (err) {
        console.error('[Softphone] Reinitialize failed:', err);
        rs.isReconnecting = false;
        attemptRecovery('retry-after-reinit-failure', err?.code);
      }
      return;
    }

    try {
      if (attempt <= 3 && deviceRef.current) {
        // Strategy 1: Try re-register existing device (lightweight)
        console.log('[Softphone] Trying re-register...');
        await deviceRef.current.register();
        console.log('[Softphone] Re-register successful');
        // registered event handler will clear error and set READY
        rs.isReconnecting = false;
        rs.attempt = 0;
      } else {
        // Strategy 2: Full reinitialize (destroy old device, create new)
        console.log('[Softphone] Escalating to full reinitialize...');
        await reinitializeDevice();
        rs.isReconnecting = false;
        rs.attempt = 0;
      }
    } catch (err) {
      console.error(`[Softphone] Recovery attempt ${attempt} failed:`, err);
      rs.isReconnecting = false;
      // Check if the failure is now fatal
      const failCode = err?.code || err?.twilioError?.code;
      if (failCode === 20101 || failCode === 31204) {
        setState(STATES.ERROR);
        setError('Session expired. Please refresh the page to reconnect.');
        rs.attempt = 0;
        return;
      }
      // Not fatal — schedule next retry
      attemptRecovery(`retry-${attempt}`, failCode);
    }
  };

  // Clean reinitialize — destroy old device before creating new one
  const reinitializeDevice = async () => {
    console.log('[Softphone] Reinitializing device (clean)...');
    // Destroy old device
    if (deviceRef.current) {
      try {
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
      } catch (err) {
        console.warn('[Softphone] Error destroying old device:', err);
      }
      deviceRef.current = null;
      setDevice(null);
    }
    // Clear timers
    if (tokenCtlRef.current) { tokenCtlRef.current.dispose(); tokenCtlRef.current = null; }
    if (initRetryTimerRef.current) { clearTimeout(initRetryTimerRef.current); initRetryTimerRef.current = null; }
    if (keepAliveRef.current) clearInterval(keepAliveRef.current);
    // Stop old Web Worker
    if (window._softphoneKeepAliveWorker) {
      window._softphoneKeepAliveWorker.postMessage('stop');
      window._softphoneKeepAliveWorker.terminate();
      window._softphoneKeepAliveWorker = null;
    }
    if (window._softphoneWorkerUrl) {
      URL.revokeObjectURL(window._softphoneWorkerUrl);
      window._softphoneWorkerUrl = null;
    }
    // Create fresh device
    await initializeDevice();
    // If initializeDevice failed internally, deviceRef won't be set
    if (!deviceRef.current) {
      // A server rate-limit (429) schedules its own single, tracked retry, and an init
      // already in flight will finish on its own — in both cases let that path own the
      // next attempt instead of also driving attemptRecovery's ladder (two loops = storm).
      if (initRetryTimerRef.current || initInFlightRef.current) return;
      throw new Error('Reinitialize failed — device not registered');
    }
  };

  // Ringtone functions
  const playRingtone = () => {
    console.log('[Softphone] 🔔 Playing ringtone...');
    try {
      // Create audio element with a reliable ringtone
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio('https://cdn.pixabay.com/audio/2022/03/24/audio_d5d3e37c06.mp3');
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 1.0; // Max volume for attention
      }
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().then(() => {
        console.log('[Softphone] ✅ Ringtone playing successfully');
      }).catch(err => {
        console.error('[Softphone] ❌ Could not play ringtone (autoplay blocked?):', err.message);
        // Try to resume AudioContext if blocked
        if (err.name === 'NotAllowedError') {
          console.log('[Softphone] Audio autoplay blocked - user interaction needed');
        }
      });
    } catch (err) {
      console.error('[Softphone] ❌ Ringtone error:', err);
    }
  };

  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  // Browser notification with aggressive attention-grabbing
  const showIncomingCallNotification = async (from, callerName, brandId, brandName, toNumber) => {
    console.log('[Softphone] Showing incoming call notification for:', from, 'brand:', brandName);

    // Try to bring window to focus
    try {
      window.focus();
    } catch (e) {
      console.log('[Softphone] Could not focus window:', e);
    }

    // Build initial popup content (will be updated with customer data)
    const callerDisplay = callerName ? `${callerName}` : from;

    // Create popup immediately with basic info
    let popupContent = buildPopupContent({
      from,
      callerDisplay,
      brandName: brandName || 'Unknown Brand',
      loading: true
    });

    // Open popup
    let popup = null;
    try {
      const popupWidth = 400;
      const popupHeight = 350;
      const left = window.screen.width - popupWidth - 20;
      const top = 20;

      popup = window.open('', 'incoming_call_popup',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=no,resizable=yes`
      );

      if (popup) {
        popup.document.write(popupContent);
        popup.document.close();
        popup.focus();
        window._incomingCallPopup = popup;
        console.log('[Softphone] ✅ Popup window opened');
      }
    } catch (e) {
      console.log('[Softphone] Could not open popup:', e);
    }

    // Fetch customer details in background and update popup
    if (brandId && from) {
      try {
        const response = await callCentreService.lookupCustomer(from, brandId);
        if (response.success && response.found && popup && !popup.closed) {
          const customer = response.customer;
          const orders = response.orders || [];
          const lastOrderDate = orders.length > 0 ? new Date(orders[0].order_date).toLocaleDateString() : null;

          // Update popup with customer data
          const updatedContent = buildPopupContent({
            from,
            callerDisplay: customer?.name || callerDisplay,
            brandName: brandName || 'Unknown Brand',
            loading: false,
            customer: {
              name: customer?.name,
              email: customer?.email,
              // lifetime covers all their orders; orders is only the latest 10 (DT-1099)
              orderCount: response.lifetime ? response.lifetime.orderCount : orders.length,
              totalSpent: totalsByCurrency(response.lifetime ? response.lifetime.totals : orders),
              lastOrderDate
            }
          });
          popup.document.body.innerHTML = '';
          popup.document.write(updatedContent);
          popup.document.close();
        }
      } catch (e) {
        console.log('[Softphone] Could not fetch customer details:', e);
      }
    }

    // Also show browser notification as backup
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('📞 INCOMING CALL', {
        body: callerName ? `${callerName}\n${from}` : `Call from ${from}`,
        icon: '/call-icon.png',
        badge: '/call-icon.png',
        requireInteraction: true,
        tag: 'incoming-call',
        vibrate: [200, 100, 200, 100, 200],
        renotify: true,
        silent: false
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  };

  // Close incoming call popup when call is handled
  const closeIncomingCallPopup = () => {
    if (window._incomingCallPopup && !window._incomingCallPopup.closed) {
      window._incomingCallPopup.close();
      window._incomingCallPopup = null;
    }
  };

  // Title flash for incoming calls
  const startTitleFlash = (from) => {
    originalTitleRef.current = document.title;
    let isOriginal = true;
    const flashInterval = setInterval(() => {
      document.title = isOriginal ? `📞 INCOMING CALL - ${from}` : originalTitleRef.current;
      isOriginal = !isOriginal;
    }, 1000);
    // Store interval ID for cleanup
    window._titleFlashInterval = flashInterval;
  };

  const stopTitleFlash = () => {
    if (window._titleFlashInterval) {
      clearInterval(window._titleFlashInterval);
      window._titleFlashInterval = null;
    }
    document.title = originalTitleRef.current || 'Call Centre';
  };

  const initializeDevice = async () => {
    if (initInFlightRef.current) {
      // NOTE (login-path coupling): on JWT login, slackUserId transitions
      // 'email_lookup' → real Slack ID while the first init is still fetching its
      // token, so the effect's second run lands here and is a no-op. That is safe
      // ONLY because both mints resolve to the same identity server-side:
      // getTwilioToken() omits the placeholder param and the token route prefers
      // the authenticated user's Slack ID. If either changes, add a re-init when
      // slackUserId differs from the in-flight one.
      console.log('[Softphone] initializeDevice already in flight — ignoring');
      return;
    }
    if (!mountedRef.current) return;
    initInFlightRef.current = true;
    try {
      setState(STATES.INITIALIZING);
      setError(null);

      console.log('[Softphone] Starting initialization for slackUserId:', slackUserId);

      // Get Twilio access token
      const tokenResponse = await callCentreService.getTwilioToken(slackUserId);
      if (!mountedRef.current) return; // unmounted while fetching — do not build a Device

      if (!tokenResponse.success) {
        throw new Error(tokenResponse.message || 'Failed to get Twilio token');
      }

      const { token, identity, expiresIn } = tokenResponse;
      resolvedAgentIdRef.current = identity.replace('agent_', '');

      // Single owner of token refresh for this Device. Every trigger (SDK
      // tokenWillExpire, backstop timer, worker age check) funnels through
      // tokenCtl.refresh(), which coalesces and rate-guards by construction.
      if (tokenCtlRef.current) tokenCtlRef.current.dispose();
      const tokenCtl = createTokenRefreshController({
        fetchToken: () => callCentreService.getTwilioToken(slackUserId),
        applyToken: (t) => { if (deviceRef.current) deviceRef.current.updateToken(t); },
        isCallActive: () => !!callRef.current,
        onExhausted: () => attemptRecovery('token-refresh-exhausted'),
        onSessionExpired: () => {
          console.log('[Softphone] Session expired, prompting re-login');
          setError('Session expired. Please log in again to continue calls.');
          setState(STATES.ERROR);
        },
        log: (m) => console.log(m),
      });
      tokenCtlRef.current = tokenCtl;
      tokenCtl.noteToken({ expiresIn });
      console.log('[Softphone] Got token for identity:', identity, 'resolvedAgentId:', resolvedAgentIdRef.current);

      // Create new Device with improved stability settings
      const newDevice = new Device(token, {
        logLevel: 1,
        codecPreferences: ['opus', 'pcmu'],
        enableRingingState: true,
        // Use specific edge servers for more stable connections
        // 'roaming' will automatically pick the best edge based on latency
        // singapore added for Philippines-based agents (lower latency)
        edge: ['singapore', 'dublin', 'frankfurt', 'ashburn', 'roaming'],
        // Warn user before closing browser with active call
        closeProtection: true,
        // Allow incoming calls when Device is busy (for call waiting scenarios)
        allowIncomingWhileBusy: false,
        // Emit tokenWillExpire 5 min before expiry (SDK default is 10s — too
        // late under Chrome's background-tab timer throttling, which can delay a
        // page timer by up to a minute). Our backstop fires at the same offset.
        tokenRefreshMs: 5 * 60 * 1000
      });

      // Set up event handlers
      setupDeviceHandlers(newDevice);

      // Register the device with a timeout
      console.log('[Softphone] Registering device...');
      const registerPromise = newDevice.register();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Registration timeout - check network connection')), 30000)
      );

      await Promise.race([registerPromise, timeoutPromise]);
      if (!mountedRef.current) {
        // Unmounted while registering — tear down what we just built.
        try { newDevice.removeAllListeners(); newDevice.destroy(); } catch (_) { /* ignore */ }
        return;
      }

      deviceRef.current = newDevice;
      setDevice(newDevice);
      setState(STATES.READY);

      console.log('[Softphone] Device registered as:', identity);

      // Token refresh is owned by tokenCtl (backstop already armed by noteToken above).

      // Start keep-alive check every 30 seconds to ensure device stays registered
      // Also sends heartbeat to server for presence tracking
      if (keepAliveRef.current) {
        clearInterval(keepAliveRef.current);
      }

      // Send initial heartbeat
      sendServerHeartbeat(identity);

      // Use Web Worker for reliable timing (not throttled in background tabs)
      // This prevents Chrome from delaying our keepalive checks
      const workerCode = `
        let interval = null;
        self.onmessage = function(e) {
          if (e.data === 'start') {
            interval = setInterval(() => self.postMessage('tick'), 15000);
          } else if (e.data === 'stop') {
            clearInterval(interval);
          }
        };
      `;
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      const keepAliveWorker = new Worker(workerUrl);
      window._softphoneKeepAliveWorker = keepAliveWorker;
      window._softphoneWorkerUrl = workerUrl;

      keepAliveWorker.onmessage = () => {
        if (deviceRef.current) {
          const deviceState = deviceRef.current.state;
          console.log('[Softphone] Keep-alive tick - Device state:', deviceState);

          // Only send heartbeat if device is registered (ready to receive calls)
          if (deviceState === 'registered') {
            sendServerHeartbeat(identity);
          }

          // Proactive token refresh from the (unthrottled) worker clock: if the
          // token is older than 50 min ask the controller — it coalesces/ignores
          // duplicates itself, so this is safe to call on every tick.
          if (tokenCtlRef.current && tokenCtlRef.current.tokenAgeMs() > 50 * 60 * 1000) {
            tokenCtlRef.current.refresh({ reason: 'worker-age-check' });
          }

          // Try to recover if device lost connection
          if (deviceState === 'unregistered' || deviceState === 'destroyed') {
            console.log('[Softphone] Keep-alive detected lost connection');
            attemptRecovery('web-worker-keepalive');
          }
        }
      };
      keepAliveWorker.postMessage('start');

      // Fallback: also use regular interval in case Web Worker fails
      keepAliveRef.current = setInterval(() => {
        if (deviceRef.current) {
          const deviceState = deviceRef.current.state;
          if (deviceState === 'registered') {
            sendServerHeartbeat(identity);
          }
        }
      }, 30000);

      // When tab becomes visible, check connection and reconnect if needed
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('[Softphone] Tab became visible, checking connection...');

          if (deviceRef.current) {
            const deviceState = deviceRef.current.state;
            console.log('[Softphone] Current device state:', deviceState);

            if (deviceState === 'registered') {
              sendServerHeartbeat(identity);
            } else {
              console.log('[Softphone] Tab visible but device not registered');
              attemptRecovery('visibility-change');
            }
          } else {
            console.log('[Softphone] Tab visible but no device');
            attemptRecovery('visibility-change-no-device');
          }
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      // Store handler reference for cleanup
      window._softphoneVisibilityHandler = handleVisibilityChange;

      // Reconnect when network comes back online
      const handleOnline = () => {
        console.log('[Softphone] Network online detected, checking connection...');
        if (deviceRef.current) {
          const deviceState = deviceRef.current.state;
          if (deviceState === 'registered') {
            sendServerHeartbeat(identity);
          } else {
            console.log('[Softphone] Network back but device not registered');
            attemptRecovery('network-online');
          }
        } else {
          attemptRecovery('network-online-no-device');
        }
      };
      window.addEventListener('online', handleOnline);
      window._softphoneOnlineHandler = handleOnline;

      // Set up beforeunload handler to mark offline when page closes
      // Uses public endpoint since sendBeacon can't include auth headers
      const handleBeforeUnload = () => {
        const agentId = identity.replace('agent_', '');
        // Get companyId from user context (stored in localStorage)
        const userStr = localStorage.getItem('user');
        const companyId = userStr ? JSON.parse(userStr).company_id : null;
        if (companyId && agentId) {
          const data = JSON.stringify({ deviceType: 'browser' });
          navigator.sendBeacon(
            `/api/call-centre/public/offline/${companyId}/${agentId}`,
            new Blob([data], { type: 'application/json' })
          );
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      // Store handler reference for cleanup
      window._softphoneBeforeUnloadHandler = handleBeforeUnload;

    } catch (err) {
      console.error('[Softphone] Initialization error:', err);
      // No Device to keep a token fresh for — don't leave a backstop timer minting tokens for nothing.
      if (tokenCtlRef.current) { tokenCtlRef.current.dispose(); tokenCtlRef.current = null; }
      if (err?.response?.status === 429) {
        // Server-side token rate limit (another tab of this identity is minting in a loop).
        // Honour Retry-After instead of showing a raw axios error. ONE tracked timer: if a
        // retry is already pending, do not stack another.
        const ra = parseInt(err.response.headers?.['retry-after'], 10);
        const waitSec = Math.min(300, Math.max(90, Number.isFinite(ra) ? ra : 90));
        setError(`Phone service is busy for this account — retrying in ${waitSec}s. If you have other Call Centre tabs open, close them.`);
        setState(STATES.OFFLINE);
        if (!initRetryTimerRef.current) {
          initRetryTimerRef.current = setTimeout(function retryInit() {
            initRetryTimerRef.current = null;
            if (!mountedRef.current || deviceRef.current) return;
            if (initInFlightRef.current) {
              // another init is mid-flight; check back shortly rather than dropping the retry
              initRetryTimerRef.current = setTimeout(retryInit, 5000);
              return;
            }
            initializeDevice();
          }, waitSec * 1000);
        }
        return;
      }
      setError(err.message || 'Failed to initialize softphone');
      setState(STATES.ERROR);
    } finally {
      initInFlightRef.current = false;
    }
  };

  const setupDeviceHandlers = (device) => {
    // Device registered and ready
    device.on('registered', () => {
      console.log('[Softphone] Device registered');
      tokenExpiredStreakRef.current = 0;
      setError(null);
      setState(STATES.READY);
      // Reset reconnection counters — successful registration via any path
      reconnectionStateRef.current.isReconnecting = false;
      reconnectionStateRef.current.attempt = 0;
    });

    // Device unregistered — use unified recovery
    device.on('unregistered', () => {
      console.log('[Softphone] Device unregistered');
      attemptRecovery('unregistered-event');
    });

    // Device error — use unified recovery with error classification
    device.on('error', (error) => {
      console.error('[Softphone] Device error:', error?.code, error?.message);
      const code = error?.code;
      // 20104 / 31205 = access token EXPIRED (e.g. laptop woke from sleep with a
      // stale token). Not fatal: mint a fresh token (force bypasses the age guard),
      // then let recovery re-register with it.
      if (code === 20104 || code === 31205) {
        // Bound: after 3 FRESH tokens (not 3 error events) that Twilio still rejects, the
        // server is issuing tokens Twilio won't accept (clock skew / key mismatch) — stop.
        if (tokenExpiredStreakRef.current >= 3) {
          console.error('[Softphone] Token still rejected after 3 fresh mints — giving up');
          setState(STATES.ERROR);
          setError('Phone service rejected a fresh token repeatedly. Please refresh the page; if it persists, contact support.');
          return;
        }
        if (tokenCtlRef.current) {
          tokenCtlRef.current.refresh({ force: true, reason: `device-error-${code}` })
            .then((minted) => { if (minted) tokenExpiredStreakRef.current += 1; })
            .catch(() => {})
            .finally(() => attemptRecovery('error-handler-token-expired', code));
        } else {
          // No controller (init failed earlier): recovery's reinit will mint — count that as the attempt.
          tokenExpiredStreakRef.current += 1;
          attemptRecovery('error-handler-token-expired', code);
        }
        return;
      }
      attemptRecovery('error-handler', code);
    });

    // Incoming call
    device.on('incoming', (call) => {
      console.log('[Softphone] 📞📞📞 INCOMING CALL 📞📞📞');
      console.log('[Softphone] From:', call.parameters.From);
      console.log('[Softphone] Call parameters:', call.parameters);
      console.log('[Softphone] Current state:', state);

      // Extract call info
      const from = call.parameters.From || 'Unknown';
      const callerName = call.parameters.CallerName || null;
      const toNumber = call.parameters.To || call.customParameters?.get('toNumber') || null;
      const brandId = call.customParameters?.get('brandId') ? parseInt(call.customParameters.get('brandId')) : null;
      const brandName = call.customParameters?.get('brandName') || null;

      console.log('[Softphone] Brand info - ID:', brandId, 'Name:', brandName, 'To:', toNumber);

      callRef.current = call;
      setActiveCall(call);
      setCallDirection('inbound');
      setCallerInfo({
        number: from,
        name: callerName
      });
      setState(STATES.RINGING);

      // Play ringtone
      playRingtone();

      // Show browser notification with customer lookup
      showIncomingCallNotification(from, callerName, brandId, brandName, toNumber);

      // Flash page title
      startTitleFlash(from);

      // Set up call handlers
      setupCallHandlers(call);

      // Notify parent component with brand info
      if (onIncomingCall) {
        onIncomingCall({
          from,
          callerName,
          brandId,
          brandName,
          toNumber
        });
      }

      // Auto-answer if coming from push notification (safe conditions)
      const urlParams = new URLSearchParams(window.location.search);
      const shouldAutoAnswer = urlParams.get('autoAnswer') === 'true';
      const timeSincePageLoad = Date.now() - pageLoadTimeRef.current;
      const withinSafetyWindow = timeSincePageLoad < 15000; // 15 second window

      if (shouldAutoAnswer && withinSafetyWindow && !autoAnswerConsumedRef.current) {
        console.log('[Softphone] Auto-answer conditions met, will answer in 1.5s');

        // Mark as consumed IMMEDIATELY to prevent double-answer
        autoAnswerConsumedRef.current = true;

        // Clear URL param BEFORE answering (prevents issues on refresh)
        urlParams.delete('autoAnswer');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);

        // Delay to ensure call is fully set up
        setTimeout(() => {
          if (callRef.current && callRef.current.status && callRef.current.status() === 'pending') {
            console.log('[Softphone] Auto-answering call now');
            stopRingtone();
            stopTitleFlash();
            closeIncomingCallPopup();
            callRef.current.accept();
          } else {
            console.log('[Softphone] Auto-answer skipped - call no longer pending');
          }
        }, 1500);
      }
    });

    // Token about to expire (SDK fires this tokenRefreshMs before expiry)
    device.on('tokenWillExpire', () => {
      console.log('[Softphone] SDK tokenWillExpire');
      if (tokenCtlRef.current) tokenCtlRef.current.refresh({ reason: 'sdk-tokenWillExpire' });
    });
  };

  const setupCallHandlers = (call) => {
    // Call accepted/connected
    call.on('accept', () => {
      console.log('[Softphone] Call accepted');
      setState(STATES.ON_CALL);
      startCallTimer(); // This now sets callStartTime internally

      if (onCallStart) {
        onCallStart({
          direction: callDirection,
          from: callerInfo?.number
        });
      }
    });

    // Call disconnected
    call.on('disconnect', () => {
      console.log('[Softphone] Call disconnected');
      handleCallEnd();
    });

    // Call cancelled (rejected by caller)
    call.on('cancel', () => {
      console.log('[Softphone] Call cancelled');
      handleCallEnd();
    });

    // Call rejected
    call.on('reject', () => {
      console.log('[Softphone] Call rejected');
      handleCallEnd();
    });

    // Call error
    call.on('error', (error) => {
      console.error('[Softphone] Call error:', error);
      setError(error.message || 'Call error');
      handleCallEnd();
    });

    // Call ringing (outbound)
    call.on('ringing', () => {
      console.log('[Softphone] Call ringing');
      setState(STATES.RINGING);
    });

    // Mute changed
    call.on('mute', (isMuted) => {
      setIsMuted(isMuted);
    });

    // Media connection lost — SDK is trying to recover (don't kill the call)
    call.on('reconnecting', (error) => {
      console.log('[Softphone] Call media reconnecting...', error?.message);
      setError('Call reconnecting...');
    });

    // Media connection restored after reconnecting
    call.on('reconnected', () => {
      console.log('[Softphone] Call media reconnected');
      setError(null);
    });
  };

  const handleCallEnd = () => {
    stopCallTimer();
    stopRingtone();
    stopTitleFlash();
    closeIncomingCallPopup();
    callRef.current = null;
    callStartTimeRef.current = null;
    setActiveCall(null);
    setCallDirection(null);
    setCallerInfo(null);
    setCallDuration(0);
    setCallStartTime(null);
    setActiveCallerId(null);
    setIsMuted(false);
    setIsOnHold(false);
    setInputDigits('');
    setError(null);
    setState(STATES.READY);

    if (onCallEnd) {
      onCallEnd();
    }

    // After call ends, check if device needs recovery (may have gone unregistered during the call)
    if (deviceRef.current && deviceRef.current.state !== 'registered') {
      console.log('[Softphone] Device not registered after call ended, triggering recovery');
      attemptRecovery('post-call-device-check');
    }
  };

  // Use a ref to store start time for timer (avoids stale closure issue)
  const callStartTimeRef = useRef(null);

  const startCallTimer = () => {
    callStartTimeRef.current = Date.now();
    setCallStartTime(callStartTimeRef.current);
    timerRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }
    }, 1000);
  };

  const stopCallTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Call actions
  const acceptCall = () => {
    if (callRef.current) {
      stopRingtone();
      stopTitleFlash();
      closeIncomingCallPopup();
      callRef.current.accept();
    }
  };

  const rejectCall = () => {
    if (callRef.current) {
      stopRingtone();
      stopTitleFlash();
      closeIncomingCallPopup();
      callRef.current.reject();
    }
  };

  const hangUp = () => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
  };

  const toggleMute = () => {
    if (callRef.current) {
      const newMuteState = !isMuted;
      callRef.current.mute(newMuteState);
      setIsMuted(newMuteState);
    }
  };

  const sendDigit = (digit) => {
    if (callRef.current && state === STATES.ON_CALL) {
      callRef.current.sendDigits(digit);
      setInputDigits(prev => prev + digit);
    } else if (state === STATES.READY) {
      setDialNumber(prev => prev + digit);
    }
  };

  const makeCall = async (number, callerIdOverride) => {
    if (!deviceRef.current || !number) return;

    try {
      setState(STATES.CONNECTING);
      setCallDirection('outbound');
      setCallerInfo({ number, name: null });
      setActiveCallerId(callerIdOverride || selectedCallerId || null);

      const call = await deviceRef.current.connect({
        params: {
          To: number,
          From: resolvedAgentIdRef.current || slackUserId,
          BrandId: selectedBrandId || '',
          CallerId: callerIdOverride || selectedCallerId || ''
        }
      });

      callRef.current = call;
      setActiveCall(call);
      setupCallHandlers(call);

    } catch (err) {
      console.error('[Softphone] Make call error:', err);
      setError(err.message || 'Failed to make call');
      setState(STATES.READY);
    }
  };

  // Store makeCall in ref for external access
  makeCallRef.current = makeCall;

  const handleDialpadCall = () => {
    if (dialNumber) {
      makeCall(dialNumber);
      setDialNumber('');
      setDialpadOpen(false);
    }
  };

  // Render status indicator
  const renderStatusIndicator = () => {
    const statusConfig = {
      [STATES.INITIALIZING]: { color: 'warning', label: 'Connecting...' },
      [STATES.READY]: { color: 'success', label: 'Ready' },
      [STATES.RINGING]: { color: 'info', label: 'Ringing' },
      [STATES.CONNECTING]: { color: 'info', label: 'Connecting' },
      [STATES.ON_CALL]: { color: 'success', label: 'On Call' },
      [STATES.ERROR]: { color: 'error', label: 'Error' },
      [STATES.OFFLINE]: { color: 'default', label: 'Offline' }
    };

    const config = statusConfig[state] || statusConfig[STATES.OFFLINE];

    return (
      <Chip
        size="small"
        icon={<CircleIcon sx={{ fontSize: 8 }} />}
        label={config.label}
        color={config.color}
        sx={{ '& .MuiChip-icon': { color: 'inherit' } }}
      />
    );
  };

  // Render dialpad
  const renderDialpad = () => {
    const buttons = [
      ['1', '2', '3'],
      ['4', '5', '6'],
      ['7', '8', '9'],
      ['*', '0', '#']
    ];

    return (
      <Box sx={{ mt: 1 }}>
        {state === STATES.READY && brandPhoneNumbers && brandPhoneNumbers.length > 1 && (
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <Select
              value={selectedCallerId}
              onChange={(e) => setSelectedCallerId(e.target.value)}
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              {brandPhoneNumbers.filter(p => p.is_active).map((phone) => (
                <MenuItem key={phone.id} value={phone.phone_number} sx={{ fontSize: '0.8rem' }}>
                  {phone.friendly_name || phone.phone_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {state === STATES.READY && (
          <TextField
            fullWidth
            size="small"
            placeholder="Enter number"
            value={dialNumber}
            onChange={(e) => setDialNumber(e.target.value)}
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: dialNumber && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setDialNumber(prev => prev.slice(0, -1))}>
                    <BackspaceIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
          {buttons.flat().map((digit) => (
            <Button
              key={digit}
              variant="outlined"
              onClick={() => sendDigit(digit)}
              sx={{
                minWidth: 0,
                py: 1,
                fontSize: '1.1rem',
                fontWeight: 500
              }}
            >
              {digit}
            </Button>
          ))}
        </Box>

        {state === STATES.READY && dialNumber && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            startIcon={<CallIcon />}
            onClick={handleDialpadCall}
            sx={{ mt: 1 }}
          >
            Call
          </Button>
        )}

        {state === STATES.ON_CALL && inputDigits && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Sent: {inputDigits}
          </Typography>
        )}
      </Box>
    );
  };

  // Render incoming call UI
  const renderIncomingCall = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Avatar
        sx={{
          width: 64,
          height: 64,
          mx: 'auto',
          mb: 1,
          bgcolor: 'primary.main',
          animation: 'pulse 1.5s infinite'
        }}
      >
        <IncomingIcon sx={{ fontSize: 32 }} />
      </Avatar>

      <Typography variant="h6" gutterBottom>
        Incoming Call
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        {callerInfo?.name || callerInfo?.number || 'Unknown'}
      </Typography>

      {callerInfo?.name && (
        <Typography variant="body2" color="text.secondary">
          {callerInfo.number}
        </Typography>
      )}

      <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={rejectCall}
          sx={{ borderRadius: 5, px: 3 }}
        >
          Decline
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CallIcon />}
          onClick={acceptCall}
          sx={{ borderRadius: 5, px: 3 }}
        >
          Answer
        </Button>
      </Stack>
    </Box>
  );

  // Render active call UI
  const renderActiveCall = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Avatar
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 1,
          bgcolor: callDirection === 'inbound' ? 'primary.main' : 'secondary.main'
        }}
      >
        {callDirection === 'inbound' ? <IncomingIcon /> : <OutgoingIcon />}
      </Avatar>

      <Typography variant="body1" fontWeight="medium">
        {callerInfo?.name || callerInfo?.number || 'Unknown'}
      </Typography>

      {callerInfo?.name && (
        <Typography variant="body2" color="text.secondary">
          {callerInfo.number}
        </Typography>
      )}

      {callDirection === 'outbound' && activeCallerId && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Calling from: {activeCallerId}
        </Typography>
      )}

      <Typography
        variant="h5"
        sx={{ my: 2, fontFamily: 'monospace', color: 'success.main' }}
      >
        {formatDuration(callDuration)}
      </Typography>

      {/* Call Controls */}
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
          <IconButton
            onClick={toggleMute}
            sx={{
              bgcolor: isMuted ? 'error.light' : 'action.hover',
              '&:hover': { bgcolor: isMuted ? 'error.main' : 'action.selected' }
            }}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Dialpad">
          <IconButton
            onClick={() => setDialpadOpen(!dialpadOpen)}
            sx={{
              bgcolor: dialpadOpen ? 'primary.light' : 'action.hover',
              '&:hover': { bgcolor: dialpadOpen ? 'primary.main' : 'action.selected' }
            }}
          >
            <DialpadIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="End Call">
          <IconButton
            onClick={hangUp}
            sx={{
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' }
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={dialpadOpen}>
        {renderDialpad()}
      </Collapse>
    </Box>
  );

  // Render ready state UI
  const renderReadyState = () => (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Phone Status
        </Typography>
        <Tooltip title="Dialpad">
          <IconButton size="small" onClick={() => setDialpadOpen(!dialpadOpen)}>
            <DialpadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={dialpadOpen}>
        {renderDialpad()}
      </Collapse>

      {!dialpadOpen && (
        <Typography variant="body2" color="success.main" sx={{ textAlign: 'center' }}>
          Ready to receive calls
        </Typography>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: state === STATES.RINGING ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
        border: state === STATES.RINGING ? '2px solid' : 'none',
        borderColor: 'primary.main',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhoneIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            Phone
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {renderStatusIndicator()}
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ mb: 1 }} />

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ mb: 1 }}
          >
            {error}
          </Alert>
        )}

        {/* State-specific content */}
        {state === STATES.INITIALIZING && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Connecting to phone system...
            </Typography>
          </Box>
        )}

        {state === STATES.READY && renderReadyState()}

        {state === STATES.RINGING && callDirection === 'inbound' && renderIncomingCall()}

        {(state === STATES.CONNECTING || state === STATES.RINGING) && callDirection === 'outbound' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={32} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              Calling {callerInfo?.number}...
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={hangUp}
              sx={{ mt: 2 }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {state === STATES.ON_CALL && renderActiveCall()}

        {state === STATES.ERROR && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                reconnectionStateRef.current.isReconnecting = false;
                reconnectionStateRef.current.attempt = 0;
                attemptRecovery('manual-retry');
              }}
            >
              Retry Connection
            </Button>
          </Box>
        )}

        {state === STATES.OFFLINE && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Phone is offline
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                reconnectionStateRef.current.isReconnecting = false;
                reconnectionStateRef.current.attempt = 0;
                attemptRecovery('manual-retry');
              }}
            >
              Connect
            </Button>
          </Box>
        )}
      </Collapse>

      {/* CSS for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </Paper>
  );
});

Softphone.displayName = 'Softphone';

export default Softphone;
